import { ReactElement, useState } from "react";
import { Box } from "@twilio-paste/core/box";
import { Tooltip, useTooltipState } from "@twilio-paste/core/tooltip";
import { Button } from "@twilio-paste/core/button";
import { AttachIcon } from "@twilio-paste/icons/esm/AttachIcon";
import { dataTestIds } from "@twiliointernal/e2-commons-shared-definitions";
import { copy, constants, AttachmentsPluginProps } from "./definitions/";
import AttachmentsPreview from "./sub/AttachmentsPreview";

import { isValidFileSize } from "../../../../utils";

const AttachmentsPlugin: React.FC<AttachmentsPluginProps> = ({
  onAttachmentSelected,
  onDeleteAttachment,
  onError,
  disabled = false,
  isSending = false,
  files,
}): ReactElement => {
  const [isLoading, setIsLoading] = useState(false);
  const tooltip = useTooltipState();

  const handleSelectAttachment = (): void => {
    tooltip.setVisible(false);

    const input = document.createElement("input");
    input.type = "file";
    input.accept = constants.acceptedFileTypes.join(",");
    input.onchange = (event) => {
      setIsLoading(true);
      tooltip.setVisible(false);

      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];

        if (!isValidFileSize(file, constants.DEFAULT_MAX_FILE_SIZE)) {
          setIsLoading(false);
          return onError(new Error("File size is too large"));
        }

        onAttachmentSelected(file);
        setIsLoading(false);
      }
    };
    input.click();
  };

  const tooltipText = `
    ${copy.tooltip.description}
    ${copy.tooltip.fileLimit(constants.DEFAULT_MAX_FILE_SIZE)}
    ${copy.tooltip.files(constants.acceptedFileTypes)}
    `;

  return (
    <>
      <Box
        position={"absolute"}
        top="space50"
        right="space160"
        className="attachment-button-container"
      >
        <Tooltip text={tooltipText} state={tooltip}>
          <Button
            loading={isLoading}
            disabled={disabled || isSending}
            variant="secondary_icon"
            size="reset"
            onClick={handleSelectAttachment}
            type="button"
            data-testid={dataTestIds.liveChat.attachmentButton}
          >
            <AttachIcon
              decorative={false}
              title={tooltipText}
              color={
                disabled || isSending ? "currentColor" : "colorTextPrimary"
              }
            />
          </Button>
        </Tooltip>
      </Box>

      {files && files.length > 0 && (
        <AttachmentsPreview
          files={files}
          onDeleteFile={onDeleteAttachment}
          disabled={disabled}
          isSending={isSending}
        />
      )}
    </>
  );
};

export default AttachmentsPlugin;
